import { t } from 'i18next';
import { isNil } from 'lodash-es';
import * as yup from 'yup';

import { AutocompleteFieldOption } from '~components/FormFields/v2/Autocomplete/types';
import { countries } from '~constants/enums';
import { Account } from '~hooks/useAccount/models';
import { CompanyBasic } from '~hooks/useCompany/models';
import { DepartmentTypeahead } from '~hooks/useDepartment/models';
import UserStore from '~store/UserStore';

const requiredAddressFieldForConntectedAccount = yup
  .string()
  .trim()
  .test(
    'required-address-field-for-connected-account',
    t('form_validation_errors.required_simple'),
    (value, context) => {
      const isConnectedAccount = context?.from?.[1]?.value?.isConnectedAccount;
      const hasValue = !isNil(value) && String(value).trim().length > 0;

      if (isConnectedAccount) {
        return hasValue;
      }

      return true;
    },
  );

export const accountDetailsSchema = yup.object().shape({
  isConnectedAccount: yup.boolean().required(),
  company: yup
    .object()
    .shape({
      label: yup.string().trim().notRequired(),
      value: yup.string().trim().required(),
    })
    .required(t('form_validation_errors.required_simple')),
  name: yup.string().trim().required(t('form_validation_errors.required_simple')),
  externalId: yup.string().trim().notRequired(),
  department: yup
    .object()
    .shape({
      label: yup.string().trim().required(),
      value: yup.string().trim().required(),
    })
    .notRequired(),
  accountingId: yup.string().trim().notRequired(),
  address: yup
    .object()
    .shape({
      placeId: yup.string().trim().notRequired(),
      premise: yup.string().trim().notRequired(),
      locality: requiredAddressFieldForConntectedAccount,
      administrativeArea: yup.string().trim().notRequired(),
      postalCode: requiredAddressFieldForConntectedAccount,
      streetAddress: requiredAddressFieldForConntectedAccount,
    })
    .when('isConnectedAccount', {
      is: true,
      then: (schema) => schema.required(t('form_validation_errors.required_simple')),
      otherwise: (schema) => schema.notRequired(),
    }),
  labels: yup
    .array()
    .of(
      yup.object({
        id: yup.string().required(),
        name: yup.string().required(),
        color: yup.string(),
      }),
    )
    .max(5)
    .notRequired(),
  isBillingAddressSame: yup.boolean().required(),
  billingAddress: yup
    .object()
    .shape({
      placeId: yup.string().trim().notRequired(),
      premise: yup.string().trim().notRequired(),
      locality: yup.string().trim().notRequired(),
      administrativeArea: yup.string().trim().notRequired(),
      postalCode: yup.string().trim().notRequired(),
      streetAddress: yup.string().trim().notRequired(),
    })
    .notRequired(),
});
export type AccountDetailsDto = yup.InferType<typeof accountDetailsSchema>;

export function setAccountDetailsSchemaDefaultValues(
  userStore: UserStore,
  isConnectedAccount?: boolean,
  account?: Account,
) {
  // @syntaxbliss `Account#company` is wrongly typed in our model as `CompanyBasic` when it should
  // be `company: { id: string; }`. This means that the company legal name will always be
  // `undefined`, hence the "Company" dropdown shows a blank option when in edit mode. This
  // workaround aims to fix this by grabbing the companys' legal name from the store, if present.
  let companyLegalNameWorkaround: string | null = null;

  if (account?.company?.id && !account.company.legalName) {
    companyLegalNameWorkaround =
      userStore.userCompanies.find((company) => {
        return company.id === account.company?.id;
      })?.legalName || null;
  }

  const companyFromStore = userStore.currentCompanies[0] || userStore.user.company;
  const company = account?.company
    ? {
        label: account.company.legalName || companyLegalNameWorkaround || '',
        value: account.company.id,
      }
    : companyFromStore
      ? { label: companyFromStore.legalName, value: companyFromStore.id }
      : null;
  const department = account?.department
    ? { label: account.department.name, value: account.department.id }
    : null;
  const address = {
    placeId: account?.address?.placeId ?? '',
    administrativeArea: account?.address?.administrativeArea ?? '',
    country: account?.address?.country ?? countries.default,
    locality: account?.address?.locality ?? '',
    postalCode: account?.address?.postalCode ?? '',
    premise: account?.address?.premise ?? '',
    streetAddress: account?.address?.streetAddress ?? '',
  };
  const billingAddress = {
    placeId: account?.billingAddress?.placeId ?? '',
    administrativeArea: account?.billingAddress?.administrativeArea ?? '',
    country: account?.billingAddress?.country ?? countries.default,
    locality: account?.billingAddress?.locality ?? '',
    postalCode: account?.billingAddress?.postalCode ?? '',
    premise: account?.billingAddress?.premise ?? '',
    streetAddress: account?.billingAddress?.streetAddress ?? '',
  };

  const defaultValues: AccountDetailsDto = {
    isConnectedAccount: isConnectedAccount ?? false,
    company: company as AutocompleteFieldOption<CompanyBasic>,
    name: account?.name ?? '',
    externalId: account?.externalId ?? '',
    department: department as AutocompleteFieldOption<DepartmentTypeahead>,
    accountingId: account?.accountingId ?? '',
    labels: account?.labels ?? [],
    address,
    isBillingAddressSame: account?.isBillingAddressSame ?? true,
    billingAddress,
  };

  return defaultValues;
}
